<template>
  <div ref="dropdownBody" class="relative" @keyup.esc="closeDropdown">
    <div class="absolute z-20 mt-3 w-full rounded-lg bg-white shadow-xs shadow-gray-300">
      <ul class="flex flex-col gap-1.5 py-3 text-xs text-neutral-700">
        <li v-for="item in modelValue" :key="item.name" class="relative list-none">
          <div
            ref="forceFocus"
            class="block cursor-pointer px-4 py-2 font-semibold hover:text-primary-500"
            :class="{ 'bg-primary-50 text-primary-500': item.active }"
            @click="clickedItem(item)"
          >
            <font-awesome-icon v-if="item.icon" :icon="item.icon" class="mr-2" />
            {{ t(item.label) }}
          </div>
          <div v-if="item.active" class="absolute md:left-full top-0">
            <slot :name="item.name" />
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { UiDropdownItem } from '@infuse/ui/domain';
import { onClickOutside } from '@vueuse/core';
import { ref, watch } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
  modelValue: UiDropdownItem[];
  ignoreCloseOnClick?: HTMLElement[];
}

const props = withDefaults(defineProps<Props>(), {
  ignoreCloseOnClick: () => []
});

interface Emits {
  (e: 'update:modelValue', items: UiDropdownItem[]): void;

  (e: 'close'): void;
}

const emit = defineEmits<Emits>();

const dropdownBody = ref();
const forceFocus = ref();
const currentActive = ref();

onClickOutside(dropdownBody, closeDropdown, { ignore: props.ignoreCloseOnClick });

watch(forceFocus, (newValue) => {
  if (newValue[0]) {
    newValue[0].focus();
  }
});

function clickedItem(item: UiDropdownItem): void {
  if (currentActive.value) {
    currentActive.value.active = false;
  }

  item.active = true;
  currentActive.value = item;
  emit('update:modelValue', props.modelValue);
}

function closeDropdown(): void {
  emit('close');
}
</script>
